import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded';
import PauseCircleOutlineRoundedIcon from '@mui/icons-material/PauseCircleOutlineRounded';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import TrendingFlatRoundedIcon from '@mui/icons-material/TrendingFlatRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  getProjectVitalFinanceForecast,
  listProjectVitalFinancePlans,
  registerProjectVitalFinancePlanOccurrence,
  setProjectVitalFinancePlanActive,
} from '../services/projectVitalService';
import type {
  ProjectVitalFinancePlan,
  ProjectVitalFinancePlanRecurrence,
  ProjectVitalFinanceViewScope,
} from '../types/projectVital';
import ProjectVitalFinancePlanDialog from './ProjectVitalFinancePlanDialog';

function formatCurrency(value: number) {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(`${value}T00:00:00`));
}

function localTodayIso() {
  const now = new Date();

  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
}

function recurrenceLabel(
  value: ProjectVitalFinancePlanRecurrence,
) {
  if (value === 'semanal') {
    return 'Semanal';
  }

  if (value === 'mensual') {
    return 'Mensual';
  }

  if (value === 'anual') {
    return 'Anual';
  }

  return 'Una vez';
}

type Props = {
  scope: ProjectVitalFinanceViewScope;
};

export default function ProjectVitalFinancePlanningSection({
  scope,
}: Props) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] =
    useState<ProjectVitalFinancePlan | null>(null);

  const plansQuery = useQuery({
    queryKey: ['project-vital', 'finance-plans', scope],
    queryFn: () => listProjectVitalFinancePlans(scope),
  });

  const forecastQuery = useQuery({
    queryKey: ['project-vital', 'finance-forecast', 60, scope],
    queryFn: () => getProjectVitalFinanceForecast(60, scope),
  });

  const activePlans = useMemo(
    () =>
      (plansQuery.data ?? []).filter(
        (plan) => plan.is_active,
      ),
    [plansQuery.data],
  );

  const inactivePlans = useMemo(
    () =>
      (plansQuery.data ?? []).filter(
        (plan) => !plan.is_active,
      ),
    [plansQuery.data],
  );

  const registerMutation = useMutation({
    mutationFn: async ({
      planId,
      occurrenceDate,
    }: {
      planId: string;
      occurrenceDate: string;
    }) => {
      return registerProjectVitalFinancePlanOccurrence(
        planId,
        occurrenceDate,
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['project-vital', 'finance'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['project-vital', 'finance-history'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['project-vital', 'finance-plans'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['project-vital', 'finance-forecast'],
        }),
      ]);

      enqueueSnackbar(
        'Movimiento previsto registrado.',
        { variant: 'success' },
      );
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.response?.data?.detail ||
          'No se pudo registrar el movimiento previsto.',
        { variant: 'error' },
      );
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({
      planId,
      isActive,
    }: {
      planId: string;
      isActive: boolean;
    }) => {
      return setProjectVitalFinancePlanActive(
        planId,
        isActive,
      );
    },
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['project-vital', 'finance-plans'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['project-vital', 'finance-forecast'],
        }),
      ]);

      enqueueSnackbar(
        variables.isActive
          ? 'Previsión reactivada.'
          : 'Previsión pausada.',
        { variant: 'success' },
      );
    },
    onError: () => {
      enqueueSnackbar(
        'No se pudo cambiar el estado de la previsión.',
        { variant: 'error' },
      );
    },
  });

  if (plansQuery.isLoading || forecastQuery.isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          minHeight: 180,
          borderRadius: 3.5,
          bgcolor: '#111c2f',
          border: `1px solid ${alpha('#94a3b8', 0.12)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={24} />
      </Paper>
    );
  }

  const forecast = forecastQuery.data;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3.5,
          bgcolor: '#111c2f',
          border: `1px solid ${alpha('#94a3b8', 0.12)}`,
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={1.5}
          >
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.8}
              >
                <EventRepeatRoundedIcon
                  sx={{
                    color: '#4DD0E1',
                    fontSize: 20,
                  }}
                />

                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 850,
                    fontSize: '1.05rem',
                  }}
                >
                  Previsión y recurrentes
                </Typography>
              </Stack>

              <Typography
                sx={{
                  mt: 0.4,
                  color: '#64748b',
                  fontSize: '0.8rem',
                }}
              >
                Gastos e ingresos esperados durante los próximos 60 días.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              disabled={scope === 'global'}
              onClick={() => {
                setEditingPlan(null);
                setDialogOpen(true);
              }}
              sx={{
                color: '#e2e8f0',
                borderColor: alpha('#94a3b8', 0.22),
              }}
            >
              Añadir previsto
            </Button>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 1,
            }}
          >
            {[
              {
                label: 'Liquidez actual',
                value: forecast?.current_liquidity ?? 0,
                icon: <TrendingFlatRoundedIcon />,
              },
              {
                label: 'Proyectada +60d',
                value: forecast?.projected_liquidity ?? 0,
                icon: <TrendingFlatRoundedIcon />,
              },
              {
                label: 'Ingresos previstos',
                value: forecast?.expected_income ?? 0,
                icon: <ArrowDownwardRoundedIcon />,
              },
              {
                label: 'Gastos previstos',
                value: forecast?.expected_expenses ?? 0,
                icon: <ArrowUpwardRoundedIcon />,
              },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  p: 1.2,
                  borderRadius: 2.7,
                  bgcolor: alpha('#ffffff', 0.022),
                  border: `1px solid ${alpha('#94a3b8', 0.09)}`,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    color: '#64748b',
                    mb: 0.7,
                    '& svg': {
                      fontSize: 18,
                    },
                  }}
                >
                  {item.icon}
                </Box>

                <Typography
                  sx={{
                    color: '#64748b',
                    fontSize: '0.68rem',
                    fontWeight: 750,
                  }}
                >
                  {item.label}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.3,
                    color: '#f8fafc',
                    fontWeight: 850,
                    fontSize: { xs: '0.98rem', sm: '1.08rem' },
                  }}
                >
                  {formatCurrency(item.value)}
                </Typography>
              </Box>
            ))}
          </Box>

          {(forecast?.accounts ?? []).length > 0 ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                  xl: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 0.8,
              }}
            >
              {(forecast?.accounts ?? []).map((account) => {
                const delta =
                  account.projected_balance -
                  account.current_balance;

                return (
                  <Box
                    key={account.id}
                    sx={{
                      p: 1.25,
                      borderRadius: 2.5,
                      bgcolor: alpha('#ffffff', 0.014),
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1}
                    >
                      <Typography
                        sx={{
                          color: '#94a3b8',
                          fontSize: '0.76rem',
                          fontWeight: 750,
                        }}
                      >
                        {account.name}
                      </Typography>

                      <Typography
                        sx={{
                          color:
                            delta > 0
                              ? '#86efac'
                              : delta < 0
                                ? '#fca5a5'
                                : '#64748b',
                          fontSize: '0.72rem',
                          fontWeight: 800,
                        }}
                      >
                        {delta > 0 ? '+' : ''}
                        {formatCurrency(delta)}
                      </Typography>
                    </Stack>

                    <Typography
                      sx={{
                        mt: 0.45,
                        color: '#e2e8f0',
                        fontWeight: 800,
                        fontSize: '0.9rem',
                      }}
                    >
                      {formatCurrency(account.projected_balance)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ) : null}

          <Box>
            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 800,
                fontSize: '0.9rem',
              }}
            >
              Próximos movimientos
            </Typography>

            {(forecast?.upcoming ?? []).length === 0 ? (
              <Typography
                sx={{
                  mt: 1,
                  color: '#64748b',
                  fontSize: '0.82rem',
                }}
              >
                No tienes movimientos previstos en los próximos 60 días.
              </Typography>
            ) : (
              <Stack
                spacing={0.7}
                sx={{ mt: 1.2 }}
              >
                {(forecast?.upcoming ?? [])
                  .slice(0, 10)
                  .map((item) => {
                    const isExpense =
                      item.movement_type === 'gasto';

                    const today = localTodayIso();
                    const isOverdue =
                      item.occurrence_date < today;
                    const isFuture =
                      item.occurrence_date > today;

                    return (
                      <Box
                        key={`${item.plan_id}-${item.occurrence_date}`}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr auto',
                            md: '115px minmax(0,1fr) 150px 120px auto',
                          },
                          gap: { xs: 0.5, md: 1.4 },
                          alignItems: 'center',
                          p: 1.15,
                          borderRadius: 2.4,
                          bgcolor: alpha('#ffffff', 0.018),
                          border: `1px solid ${alpha('#94a3b8', 0.07)}`,
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#64748b',
                            fontSize: '0.74rem',
                          }}
                        >
                          {formatDate(item.occurrence_date)}
                          {isOverdue ? ' · vencido' : ''}
                        </Typography>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            noWrap
                            sx={{
                              color: '#e2e8f0',
                              fontWeight: 750,
                              fontSize: '0.84rem',
                            }}
                          >
                            {item.name}
                          </Typography>

                          <Typography
                            noWrap
                            sx={{
                              mt: 0.15,
                              color: '#64748b',
                              fontSize: '0.7rem',
                            }}
                          >
                            {item.account_name} · {item.category}
                          </Typography>
                        </Box>

                        <Chip
                          label={recurrenceLabel(item.recurrence)}
                          size="small"
                          variant="outlined"
                          sx={{
                            display: { xs: 'none', md: 'inline-flex' },
                            color: '#94a3b8',
                            borderColor: alpha('#94a3b8', 0.14),
                          }}
                        />

                        <Typography
                          sx={{
                            color: isExpense
                              ? '#fca5a5'
                              : '#86efac',
                            fontWeight: 850,
                            textAlign: 'right',
                            whiteSpace: 'nowrap',
                            gridRow: { xs: '1 / 3', md: 'auto' },
                            gridColumn: { xs: '2 / 3', md: 'auto' },
                          }}
                        >
                          {isExpense ? '−' : '+'}
                          {formatCurrency(item.amount)}
                        </Typography>

                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CheckRoundedIcon />}
                          disabled={
                            registerMutation.isPending ||
                            isFuture
                          }
                          onClick={() =>
                            registerMutation.mutate({
                              planId: item.plan_id,
                              occurrenceDate: item.occurrence_date,
                            })
                          }
                          sx={{
                            display: { xs: 'none', md: 'inline-flex' },
                            borderColor: alpha('#4DD0E1', 0.22),
                            color: '#4DD0E1',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {isFuture ? 'Pendiente' : 'Registrar'}
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          disabled={
                            registerMutation.isPending ||
                            isFuture
                          }
                          onClick={() =>
                            registerMutation.mutate({
                              planId: item.plan_id,
                              occurrenceDate: item.occurrence_date,
                            })
                          }
                          sx={{
                            display: { xs: 'inline-flex', md: 'none' },
                            mt: 0.4,
                            width: 'fit-content',
                            borderColor: alpha('#4DD0E1', 0.22),
                            color: '#4DD0E1',
                          }}
                        >
                          {isFuture ? 'Pendiente' : 'Registrar'}
                        </Button>
                      </Box>
                    );
                  })}

                {(forecast?.upcoming ?? []).length > 10 ? (
                  <Typography
                    sx={{
                      color: '#64748b',
                      fontSize: '0.72rem',
                      pl: 0.5,
                    }}
                  >
                    + {(forecast?.upcoming ?? []).length - 10} movimientos previstos adicionales
                  </Typography>
                ) : null}
              </Stack>
            )}
          </Box>

          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 800,
                  fontSize: '0.9rem',
                }}
              >
                Planificaciones activas
              </Typography>

              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.72rem',
                }}
              >
                {activePlans.length}
              </Typography>
            </Stack>

            {activePlans.length === 0 ? (
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.82rem',
                }}
              >
                Todavía no has definido gastos o ingresos previstos.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                  },
                  gap: 0.8,
                }}
              >
                {activePlans.map((plan) => (
                  <Box
                    key={plan.id}
                    sx={{
                      p: 1.25,
                      borderRadius: 2.5,
                      bgcolor: alpha('#ffffff', 0.016),
                      border: `1px solid ${alpha('#94a3b8', 0.07)}`,
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={1}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          noWrap
                          sx={{
                            color: '#e2e8f0',
                            fontWeight: 800,
                            fontSize: '0.82rem',
                          }}
                        >
                          {plan.name}
                        </Typography>

                        <Typography
                          sx={{
                            mt: 0.25,
                            color: '#64748b',
                            fontSize: '0.7rem',
                          }}
                        >
                          {recurrenceLabel(plan.recurrence)} · {formatCurrency(plan.amount)}
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={0.2}>
                        <IconButton
                          size="small"
                          aria-label="Editar previsto"
                          onClick={() => {
                            setEditingPlan(plan);
                            setDialogOpen(true);
                          }}
                          sx={{
                            color: '#64748b',
                            '&:hover': {
                              color: '#4DD0E1',
                            },
                          }}
                        >
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          aria-label="Pausar previsto"
                          disabled={statusMutation.isPending}
                          onClick={() =>
                            statusMutation.mutate({
                              planId: plan.id,
                              isActive: false,
                            })
                          }
                          sx={{
                            color: '#64748b',
                          }}
                        >
                          <PauseCircleOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </Box>
            )}

            {inactivePlans.length > 0 ? (
              <Stack spacing={0.6} sx={{ mt: 1.4 }}>
                <Typography
                  sx={{
                    color: '#475569',
                    fontSize: '0.7rem',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: 0.7,
                  }}
                >
                  Pausadas / completadas
                </Typography>

                {inactivePlans.slice(0, 5).map((plan) => (
                  <Stack
                    key={plan.id}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{
                      px: 0.6,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#64748b',
                        fontSize: '0.76rem',
                      }}
                    >
                      {plan.name}
                    </Typography>

                    {plan.recurrence !== 'unico' ? (
                      <IconButton
                        size="small"
                        aria-label="Reactivar previsto"
                        disabled={statusMutation.isPending}
                        onClick={() =>
                          statusMutation.mutate({
                            planId: plan.id,
                            isActive: true,
                          })
                        }
                        sx={{ color: '#64748b' }}
                      >
                        <PlayCircleOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </Stack>
                ))}
              </Stack>
            ) : null}
          </Box>
        </Stack>
      </Paper>

      <ProjectVitalFinancePlanDialog
        open={dialogOpen}
        financialScope={
          editingPlan?.financial_scope ??
          (scope === 'professional' ? 'professional' : 'personal')
        }
        plan={editingPlan}
        onClose={() => {
          setDialogOpen(false);
          setEditingPlan(null);
        }}
      />
    </>
  );
}
